/**
 * money.ts — the SINGLE source of truth for money in the admin SPA.
 *
 * Mirrors `aapastech-backend/src/lib/money.ts`. The whole reason it exists:
 * "divide-by-100" display bugs kept resurfacing because every component
 * re-implemented the minor→major conversion inline. This module makes that
 * decision once.
 *
 * The contract (same as the backend):
 *   - Money arrives from the API as an INTEGER count of MINOR units (paise/cents)
 *     plus a currency code. NEVER divide by 100 inline in a component — call
 *     `formatMoney(minor, currency)`.
 *   - JPY has multiplier 1 (never divided).
 *   - Credits are counts where 1 credit == 1 MAJOR unit of the team's locked
 *     currency; `formatCredits` renders "33 credits (≈ ₹33)".
 *
 * EXCEPTION worth knowing: plugin add-on TIER prices (the plugin billing cards)
 * come from the pricing catalogue in MAJOR units, not minor — so those are
 * displayed as-is (see PluginBilling.tsx). This module is for MINOR-unit amounts:
 * platform plan prices, statements, invoices, orders, coupons, payment links.
 */

export type CurrencyCode = 'INR' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'JPY' | 'AUD' | 'SGD' | 'AED';

export const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  JPY: '¥',
  AUD: 'A$',
  SGD: 'S$',
  AED: 'د.إ',
};

export function getCurrencySymbol(currency?: string): string {
  return CURRENCY_SYMBOLS[(currency ?? 'USD').toUpperCase()] ?? '$';
}

// TRIPWIRE: if a THREE-decimal currency (KWD/BHD/OMR — multiplier 1000) is ever
// supported, update this AND the backend lib/money.ts together — a silent ×100
// default would mis-price that currency 10×.
export function getCurrencyMultiplier(currency?: string): number {
  return (currency ?? '').toUpperCase() === 'JPY' ? 1 : 100;
}

/** Minor → major (may be fractional). e.g. toMajor(1999, 'USD') === 19.99. */
export function toMajor(minor: number, currency?: string): number {
  return minor / getCurrencyMultiplier(currency);
}

/**
 * Format a MINOR-unit amount for display. THE canonical formatter. Shows no
 * decimals for whole amounts (₹3,999) and two decimals when fractional
 * (₹19.99). Pass `{ alwaysDecimals: true }` for invoice rows that want ".00".
 */
export function formatMoney(
  minor: number,
  currency?: string,
  opts?: { alwaysDecimals?: boolean }
): string {
  const cur = (currency ?? 'USD').toUpperCase();
  const symbol = getCurrencySymbol(cur);
  const major = toMajor(minor ?? 0, cur);
  const fractionDigits =
    cur === 'JPY' ? 0 : opts?.alwaysDecimals || !Number.isInteger(major) ? 2 : 0;
  const num = major.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${symbol}${num}`;
}

/** Credits needed to pay a MINOR-unit price. 1 credit buys 1 major unit. */
export function creditsForPrice(minor: number, currency?: string): number {
  return Math.ceil(minor / getCurrencyMultiplier(currency));
}

/**
 * Format a credit balance. Credits are counts, but 1 credit = 1 major unit of
 * the team's locked currency — so when we know that currency we surface the
 * equivalent ("33 credits (≈ ₹33)") instead of an ambiguous bare "33".
 */
export function formatCredits(count: number, currency?: string): string {
  const n = (count ?? 0).toLocaleString('en-US');
  if (!currency) return `${n} credits`;
  const approx = formatMoney((count ?? 0) * getCurrencyMultiplier(currency), currency);
  return `${n} credits (≈ ${approx})`;
}
